import { useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from './Icon';
import { IsomerThumbnail } from './IsomerThumbnail';
import { MoleculeFormulaLabel } from './MoleculeFormulaLabel';
import { ReactionThumbnail } from './ReactionThumbnail';
import type {
  IllustrationElementPick,
  IllustrationPosition
} from '@peruselab/structure-editor/domain/illustrationElement';
import type { ArmedIllustrationPick } from '../hooks/useIllustrationComposer';
import type { PaperSize } from '@peruselab/structure-editor/domain/geometry';
import { clientToNormalizedPoint } from '@peruselab/structure-editor/domain/geometry';
import { normalizedToPixel } from '@peruselab/structure-editor/domain/bonds';

export const ILLUSTRATION_CANVAS_SIZE: PaperSize = { width: 900, height: 600 };
const ILLUSTRATION_ELEMENT_SIZE = { width: 160, height: 130 };
const DRAG_MOVEMENT_TOLERANCE_PX = 3;

interface IllustrationCanvasProps {
  readonly elements: readonly IllustrationElementPick[];
  readonly armedPick: ArmedIllustrationPick | null;
  readonly onPlaceAt: (position: IllustrationPosition) => void;
  readonly onMoveElement: (localId: string, position: IllustrationPosition) => void;
  readonly onRemoveElement: (localId: string) => void;
  readonly onUpdateLabel: (localId: string, label: string) => void;
}

interface ElementDrag {
  readonly localId: string;
  readonly pointerId: number;
  readonly startPointerPixel: IllustrationPosition;
  readonly startElementPosition: IllustrationPosition;
  readonly currentElementPosition: IllustrationPosition;
  readonly moved: boolean;
}

function clampToCanvas(position: IllustrationPosition): IllustrationPosition {
  const halfWidth = ILLUSTRATION_ELEMENT_SIZE.width / 2;
  const halfHeight = ILLUSTRATION_ELEMENT_SIZE.height / 2;
  return {
    x: Math.min(ILLUSTRATION_CANVAS_SIZE.width - halfWidth, Math.max(halfWidth, position.x)),
    y: Math.min(ILLUSTRATION_CANVAS_SIZE.height - halfHeight, Math.max(halfHeight, position.y))
  };
}

function hitTest(
  point: IllustrationPosition,
  elements: readonly IllustrationElementPick[]
): IllustrationElementPick | null {
  const halfWidth = ILLUSTRATION_ELEMENT_SIZE.width / 2;
  const halfHeight = ILLUSTRATION_ELEMENT_SIZE.height / 2;
  for (const element of [...elements].reverse()) {
    if (Math.abs(point.x - element.position.x) <= halfWidth && Math.abs(point.y - element.position.y) <= halfHeight) {
      return element;
    }
  }
  return null;
}

// #140: the free-position placement/drag canvas for Illustration composition. Mirrors
// DrawingPaper's AtomDrag interaction shape (pointerdown-hit branches into drag, pointermove
// updates a live preview position, pointerup commits only if moved) but far simpler -- one
// free-standing rectangular block per element, no graph/bond coupling. Rendered as plain HTML with
// CSS-absolute-positioned children (not SVG): IsomerThumbnail/MoleculeFormulaLabel/ReactionThumbnail
// are all plain React components that inject SVG internally via dangerouslySetInnerHTML, so nesting
// them in an SVG canvas would need <foreignObject> for no benefit.
export function IllustrationCanvas({
  elements,
  armedPick,
  onPlaceAt,
  onMoveElement,
  onRemoveElement,
  onUpdateLabel
}: IllustrationCanvasProps) {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const [drag, setDrag] = useState<ElementDrag | null>(null);

  const pointFromEvent = (event: ReactPointerEvent<HTMLDivElement>): IllustrationPosition => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    const normalized = clientToNormalizedPoint(event.clientX, event.clientY, rect);
    return normalizedToPixel(normalized, ILLUSTRATION_CANVAS_SIZE);
  };

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    const point = pointFromEvent(event);
    const hit = hitTest(point, elements);
    if (hit) {
      event.currentTarget.setPointerCapture(event.pointerId);
      setDrag({
        localId: hit.localId,
        pointerId: event.pointerId,
        startPointerPixel: point,
        startElementPosition: hit.position,
        currentElementPosition: hit.position,
        moved: false
      });
      return;
    }
    if (armedPick) {
      onPlaceAt(clampToCanvas(point));
    }
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag || drag.pointerId !== event.pointerId) return;
    const point = pointFromEvent(event);
    const dx = point.x - drag.startPointerPixel.x;
    const dy = point.y - drag.startPointerPixel.y;
    const nextPosition = clampToCanvas({
      x: drag.startElementPosition.x + dx,
      y: drag.startElementPosition.y + dy
    });
    const moved = drag.moved || Math.hypot(dx, dy) > DRAG_MOVEMENT_TOLERANCE_PX;
    setDrag({ ...drag, currentElementPosition: nextPosition, moved });
  };

  const finishDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (drag.moved) onMoveElement(drag.localId, drag.currentElementPosition);
    setDrag(null);
  };

  return (
    <div
      ref={canvasRef}
      className="illustration-canvas"
      style={{ width: ILLUSTRATION_CANVAS_SIZE.width, height: ILLUSTRATION_CANVAS_SIZE.height }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={finishDrag}
      onPointerCancel={finishDrag}
    >
      {elements.map((element) => {
        const position = drag?.localId === element.localId ? drag.currentElementPosition : element.position;
        return (
          <div
            key={element.localId}
            className={
              drag?.localId === element.localId
                ? 'illustration-element illustration-element-dragging'
                : 'illustration-element'
            }
            style={{
              width: ILLUSTRATION_ELEMENT_SIZE.width,
              height: ILLUSTRATION_ELEMENT_SIZE.height,
              left: position.x,
              top: position.y
            }}
          >
            {element.kind === 'isomer' ? (
              <IsomerThumbnail moleculeId={element.moleculeId} isomerId={element.isomerId} size={80} />
            ) : null}
            {element.kind === 'molecule' ? <MoleculeFormulaLabel moleculeId={element.moleculeId} /> : null}
            {element.kind === 'reaction' ? <ReactionThumbnail reactionId={element.reactionId} /> : null}
            <input
              className="illustration-element-label-input"
              placeholder={t('illustrationCanvas.labelPlaceholder')}
              value={element.label ?? ''}
              onChange={(event) => onUpdateLabel(element.localId, event.target.value)}
              onPointerDown={(event) => event.stopPropagation()}
            />
            <button
              type="button"
              className="illustration-element-remove"
              aria-label={t('illustrationCanvas.removeElement')}
              title={t('illustrationCanvas.removeElement')}
              onPointerDown={(event) => event.stopPropagation()}
              onClick={() => onRemoveElement(element.localId)}
            >
              <Icon name="close" size="1rem" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
